import cn from 'classnames'
import React, { useMemo } from 'react'

import { useEditorCanvasMouse } from 'components/Editor/EditorContext/actions'
import { hasContent } from 'components/Editor/EditorContext/selectors/block'
import { ColumnActionUiEnum } from 'components/Editor/types'
import { scrollProps } from 'services/Scroll/ScrollService'
import { AppMode } from 'services/Store/Project/enums'
import { useProjectContext } from 'services/Store/Project/hooks'
import { getIsActiveNode, getIsHighlighted } from 'services/Store/Project/selectors'
import { selectRealtimeUsers } from 'services/Store/Users/selectors'
import { useAppSelector } from 'services/Store/hooks'
import { t } from 'services/Translation'

import type { IColumnProps } from '../../Node/types'
import ColumnControls from '../../controls/ColumnControls'
import ControlSelection from '../../controls/ControlSelection'
import * as s from './EditorColumn.module.scss'

const EditorColumn: React.FC<IColumnProps> = ({ children, node, style, level, block, mode }) => {
  const isPro = mode.editorMode === AppMode.pro
  const isActive = useProjectContext(getIsActiveNode, node.id)
  const isHighlighted = useProjectContext(getIsHighlighted, node.id)

  const users = useAppSelector((state) => selectRealtimeUsers(state, { nodeId: node.id }))
  const isRoot = block.schema.rootId === node.id
  const handlers = useEditorCanvasMouse({
    mode: mode.editorMode,
    blockId: block.uuid,
    nodeId: node.id,
  })

  const controls = useMemo(() => {
    const arr: ColumnActionUiEnum[] = []
    if (isActive && isPro) {
      arr.push(ColumnActionUiEnum.ADD_ABSOLUTE)
      if (level > 0) {
        arr.push(ColumnActionUiEnum.DELETE)
        arr.push(ColumnActionUiEnum.ADD_COL_NEXT)
        arr.push(ColumnActionUiEnum.ADD_COL_PREV)
        arr.push(ColumnActionUiEnum.ADD_ROW_ABOVE)
        arr.push(ColumnActionUiEnum.ADD_ROW_BELOW)
        arr.push(ColumnActionUiEnum.SPLIT_HORIZONTAL)
        arr.push(ColumnActionUiEnum.SPLIT_VERTICAL)
      }
      if (!isRoot && !hasContent(block, node.id)) {
        arr.push(ColumnActionUiEnum.ADD_SHELL)
        arr.push(ColumnActionUiEnum.SPLIT_HORIZONTAL)
        arr.push(ColumnActionUiEnum.SPLIT_VERTICAL)
      }
    }
    return arr
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRoot, isActive && isPro, block, node.id])

  return (
    <div
      {...scrollProps(node.id)}
      {...handlers}
      className={cn(s.column, {
        [s.fillMode]: mode.editorMode === AppMode.fill,
        [s.showGrid]: isPro,
      })}
      style={style}
    >
      {children}
      {isPro && Boolean(isActive || isHighlighted || users.length) && (
        <ColumnControls block={block} controls={controls} nodeId={node.id} />
      )}
      {(isActive || isHighlighted || Boolean(users && users.length)) && (
        <ControlSelection
          active={isActive}
          highlighted={isHighlighted}
          label={isRoot ? t('editor.root') : t('editor.column')}
          name='colSelection'
          users={users}
        />
      )}
    </div>
  )
}

export default React.memo(EditorColumn)
